/**
 * Shoppable Bundle — sections/shoppable-bundle.liquid
 *
 * Click-only shoppable collage. A pin (or a product-list CTA) opens the theme's
 * existing quickview dialog (sections/product-quickview.liquid) so the shopper
 * can pick a size and add to cart. Works identically for mouse, touch and
 * keyboard: there is no `mouseenter` and no `touchstart` listener in this file.
 *
 * Never calls BlsSubActionProduct.handleInitQuickviewAction() / .init() — both
 * are page-global and non-idempotent (theme.js:1576+, anonymous arrow, no
 * de-dup guard). Instead we bind ONE capture-phase click listener on the
 * section root and stopPropagation(), so the theme's own target-phase listener
 * on the same button never receives the event. Exactly one quickview fetch per
 * activation, while the theme's binding stays available as the no-JS-of-ours
 * fallback.
 *
 * Load-order note: this file is deferred from inside {{ content_for_layout }},
 * which runs BEFORE snippets/scripts-tag.liquid. Every theme global
 * (EasyDialogBox, Shopify, BlsLazyloadImg, window.routes, …) is therefore
 * resolved lazily, inside the handler that needs it, never at parse time.
 */
(function () {
  'use strict';

  /* Guard: script included twice on the same page. */
  if (!window.customElements || window.customElements.get('shoppable-bundle')) return;

  var DIALOG_ID = 'qvdialog_0';
  var DIALOG_NAME = 'qvdialog';
  var DIALOG_CLASSES = 'dlg dlg-disable-heading dlg-multi dlg-disable-footer dlg-disable-drag';
  var DIALOG_WAIT_MS = 8000;

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var TRIGGER_SELECTOR = '[data-sbundle-qv], [data-kind="note"]';
  var INDEXED_SELECTOR = '[data-sbundle-pin], [data-sbundle-row], [data-sbundle-row-cta]';

  var STR = {
    dialogLabel: 'Quick view',
    close: 'Close',
    added: 'Added to cart.',
    genericError: 'Sorry, we could not add these items to your cart. Please try again.',
    namedError: 'Sorry, we could not add these items to your cart: '
  };

  function media(query) {
    try {
      return !!(window.matchMedia && window.matchMedia(query).matches);
    } catch (e) {
      return false;
    }
  }

  function isFinePointer() {
    return media('(hover: hover) and (pointer: fine)');
  }

  function prefersReducedMotion() {
    return media('(prefers-reduced-motion: reduce)');
  }

  function pad2(value) {
    var n = Math.max(0, Math.floor(value));
    return n < 10 ? '0' + n : String(n);
  }

  function isRendered(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return !!(rect.width || rect.height);
  }

  function closestIn(node, selector) {
    if (!node) return null;
    var el = node.nodeType === 1 ? node : node.parentElement;
    if (!el || !el.closest) return null;
    return el.closest(selector);
  }

  function safe(fn) {
    try {
      return fn();
    } catch (e) {
      return undefined;
    }
  }

  class ShoppableBundle extends HTMLElement {
    constructor() {
      super();

      this.finePointer = isFinePointer();
      this.reducedMotion = prefersReducedMotion();

      this.activeIndex = null;
      this.openLabels = [];
      this.countdownInterval = null;
      this.countdownEndsAt = null;
      this.units = null;
      this.pulseObserver = null;
      this.dialogObserver = null;
      this.dialogPoll = null;
      this.dialogBox = null;
      this.dialogReturnFocus = null;
      this.scrollLocked = false;
      this.scrollY = 0;
      this.bodyStyle = null;
      this.isAdding = false;
      this.setupDone = false;
      this.suppressDocumentClick = false;
      this.designMode = false;
      this.strClose = STR.close;
      this.strDialog = STR.dialogLabel;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      /* Capture phase — see the file header. Bound on the root, so it works
         even if this constructor runs before the children are parsed. */
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerout', this.onPointerOut);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
    }

    connectedCallback() {
      /* Children are guaranteed present here for both the parser path (deferred
         upgrade) and the customizer path (innerHTML then insert). */
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
    }

    /* ------------------------------------------------------------------ */
    /* Setup / teardown                                                    */
    /* ------------------------------------------------------------------ */

    setup() {
      if (this.setupDone) return;
      this.setupDone = true;

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      /* Liquid passes the theme's own locale strings down, so no new locale key
         is needed and translated stores get translated dialog chrome. */
      this.strClose = (this.getAttribute('data-str-close') || '').trim() || STR.close;
      this.strDialog = (this.getAttribute('data-str-dialog') || '').trim() || STR.dialogLabel;
      this.designMode = this.getAttribute('data-design-mode') === 'true';

      this.setupPanels();
      this.setupFirstPinLabel();
      this.setupPulse();
      this.setupCountdown();
      this.updateAddBothState();

      safe(function () {
        window.BlsLazyloadImg && window.BlsLazyloadImg.init();
      });
    }

    teardown() {
      this.setupDone = false;

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      if (this.pulseObserver) {
        safe(this.pulseObserver.disconnect.bind(this.pulseObserver));
        this.pulseObserver = null;
      }
      /* A dialog opened from this section may outlive the section in the
         customizer — release the body scroll lock and the trap either way. */
      this.releaseDialog(false);
    }

    /* ------------------------------------------------------------------ */
    /* Click delegation                                                    */
    /* ------------------------------------------------------------------ */

    onClick(event) {
      /* Panel controls come first. They are our own markup with no theme
         listener competing for them, so unlike the pins they need no
         stopPropagation and must not get one -- the theme's outside-click
         closers should see these clicks normally. */
      var opt = closestIn(event.target, '[data-sbundle-opt]');
      if (opt && this.contains(opt)) {
        event.preventDefault();
        this.selectOption(opt);
        return;
      }

      var add = closestIn(event.target, '[data-sbundle-panel-add]');
      if (add && this.contains(add)) {
        event.preventDefault();
        this.addPanelVariant(add);
        return;
      }

      var trigger = closestIn(event.target, TRIGGER_SELECTOR);
      if (!trigger || !this.contains(trigger)) return;

      /* Runs before the theme's target-phase listener on the very same button
         (theme.js:1580). stopPropagation() means that listener never fires, so
         a pin carrying .bls__product-quickview cannot double-open. */
      event.preventDefault();
      event.stopPropagation();

      /* ...but stopping propagation also hides the click from the theme's
         document-level "click outside to close" handlers (the header search
         popup at theme.js:2737, closeAllSelect at product.js:2360). Re-broadcast
         a plain click from <body> so those still close. <body> is not an
         ancestor path of this element's listeners, so this cannot re-enter. */
      this.broadcastOutsideClick();

      if (trigger.classList.contains('is-busy')) return;

      this.dismissFirstPinLabel();

      var anchor = trigger.closest('.sbundle__pin-anchor');
      if (anchor) anchor.classList.add('has-interacted');
      trigger.classList.add('has-interacted');

      if (trigger.getAttribute('data-kind') === 'note') {
        this.toggleNote(trigger);
        return;
      }

      /* propagation is stopped above, so the document-level closer never sees
         this click — close any open callout explicitly. */
      this.closeNotes();

      if (this.panelMode()) {
        this.activatePanel(trigger);
        return;
      }

      this.openQuickview(trigger);
    }

    /**
     * Replays the click at document level with <body> as the target. Every theme
     * handler this exists for reads `event.target.closest(...)`, which resolves
     * to null for <body> — exactly the "clicked outside" answer they want.
     */
    broadcastOutsideClick() {
      var self = this;
      safe(function () {
        if (!document.body) return;
        var replay = new MouseEvent('click', { bubbles: true, cancelable: true });
        replay.sbundleReplay = true;
        self.suppressDocumentClick = true;
        document.body.dispatchEvent(replay);
        self.suppressDocumentClick = false;
      });
    }

    onDocumentClick(event) {
      if (this.suppressDocumentClick || event.sbundleReplay) return;
      if (closestIn(event.target, '.sbundle__pin-anchor--note')) return;
      this.closeNotes();
    }

    onKeydown(event) {
      var key = event.key;
      if (key !== 'Escape' && key !== 'Esc') return;
      /* Never swallow ESC while the quickview dialog is open — EasyDialogBox
         binds its own document keyup handler for that. */
      if (document.getElementById(DIALOG_ID)) return;

      var openNote = this.querySelector('.sbundle__pin[data-kind="note"][aria-expanded="true"]');
      if (openNote) {
        event.stopPropagation();
        this.closeNotes();
        safe(openNote.focus.bind(openNote));
        return;
      }
      this.dismissFirstPinLabel();
    }

    /* ------------------------------------------------------------------ */
    /* Callout notes (disclosure)                                          */
    /* ------------------------------------------------------------------ */

    notePanelFor(button) {
      var id = button.getAttribute('aria-controls');
      var panel = id ? document.getElementById(id) : null;
      if (panel && this.contains(panel)) return panel;
      var anchor = button.closest('.sbundle__pin-anchor');
      return anchor ? anchor.querySelector('.sbundle__note') : null;
    }

    closeNotes() {
      var buttons = this.querySelectorAll('.sbundle__pin[data-kind="note"]');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-expanded', 'false');
        var panel = this.notePanelFor(buttons[i]);
        if (panel) panel.hidden = true;
      }
    }

    toggleNote(button) {
      var wasOpen = button.getAttribute('aria-expanded') === 'true';
      this.closeNotes();
      if (wasOpen) return;
      var panel = this.notePanelFor(button);
      if (!panel) return;
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
    }

    /* ------------------------------------------------------------------ */
    /* Pin <-> list cross-highlight                                        */
    /* ------------------------------------------------------------------ */

    indexFromTarget(node) {
      var el = closestIn(node, INDEXED_SELECTOR);
      if (!el || !this.contains(el)) return null;
      return (
        el.getAttribute('data-sbundle-pin') ||
        el.getAttribute('data-sbundle-row') ||
        el.getAttribute('data-sbundle-row-cta') ||
        null
      );
    }

    setActiveIndex(index) {
      if (this.activeIndex === index) return;
      this.activeIndex = index;

      var pins = this.querySelectorAll('[data-sbundle-pin]');
      for (var i = 0; i < pins.length; i++) {
        var anchor = pins[i].closest('.sbundle__pin-anchor') || pins[i];
        var on = index !== null && pins[i].getAttribute('data-sbundle-pin') === index;
        anchor.classList.toggle('is-active', on);
      }

      var rows = this.querySelectorAll('[data-sbundle-row]');
      for (var j = 0; j < rows.length; j++) {
        var rowOn = index !== null && rows[j].getAttribute('data-sbundle-row') === index;
        rows[j].classList.toggle('is-active', rowOn);
      }
    }

    clearActive() {
      this.setActiveIndex(null);
    }

    onPointerOver(event) {
      if (!this.finePointer) return;
      var index = this.indexFromTarget(event.target);
      if (index === null) return;
      this.dismissFirstPinLabel();
      this.setActiveIndex(index);
    }

    onPointerOut(event) {
      if (!this.finePointer) return;
      if (this.activeIndex === null) return;
      var to = event.relatedTarget;
      if (to && this.contains(to) && this.indexFromTarget(to) === this.activeIndex) return;
      this.clearActive();
    }

    onFocusIn(event) {
      var index = this.indexFromTarget(event.target);
      if (index === null) return;
      this.dismissFirstPinLabel();
      this.setActiveIndex(index);
    }

    onFocusOut(event) {
      if (this.activeIndex === null) return;
      var to = event.relatedTarget;
      if (to && this.contains(to) && this.indexFromTarget(to) === this.activeIndex) return;
      this.clearActive();
    }

    /* ------------------------------------------------------------------ */
    /* First-pin label (desktop pointers only)                             */
    /* ------------------------------------------------------------------ */

    setupFirstPinLabel() {
      /* Labels are a desktop affordance. On a phone an auto-opened label covers
         the garment the pin is pointing at, which is the opposite of helpful. */
      if (!this.finePointer) return;

      var open = [];

      /* Per-pin "Start open" wins, and applies to callout pins too. */
      var flagged = this.querySelectorAll('.sbundle__pin[data-start-open="true"]');
      for (var i = 0; i < flagged.length; i++) open.push(flagged[i]);

      /* Section-level "Show the first pin's label by default" is the fallback,
         and only when no pin has asked for it explicitly. */
      if (!open.length && this.dataset.openFirstPin === 'true') {
        var first = this.querySelector('.sbundle__pin[data-kind="product"]');
        if (first) open.push(first);
      }

      for (var j = 0; j < open.length; j++) open[j].classList.add('is-open');
      this.openLabels = open;
    }

    dismissFirstPinLabel() {
      if (!this.openLabels || !this.openLabels.length) return;
      for (var i = 0; i < this.openLabels.length; i++) {
        this.openLabels[i].classList.remove('is-open');
      }
      this.openLabels = [];
    }

    /* ------------------------------------------------------------------ */
    /* Pulse                                                               */
    /* ------------------------------------------------------------------ */

    setupPulse() {
      if (this.dataset.pinPulse !== 'true') return;
      if (this.reducedMotion) return;
      /* The customizer re-renders the section on every keystroke; without this a
         merchant editing the heading watches the pins pulse continuously. */
      if (this.designMode || (window.Shopify && window.Shopify.designMode)) return;
      if (!('IntersectionObserver' in window)) return;
      if (!this.querySelector('.sbundle__pin-ripple')) return;

      var self = this;
      this.pulseObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            self.classList.add('sbundle--pulse');
            if (self.pulseObserver) {
              self.pulseObserver.disconnect();
              self.pulseObserver = null;
            }
            return;
          }
        },
        { threshold: 0.25 }
      );
      this.pulseObserver.observe(this);
    }

    /* ------------------------------------------------------------------ */
    /* Countdown                                                           */
    /* ------------------------------------------------------------------ */

    setupCountdown() {
      var el = this.querySelector('[data-sbundle-countdown]');
      if (!el) return;

      var remaining = parseInt(el.getAttribute('data-remaining'), 10);
      if (!isFinite(remaining) || remaining <= 0) {
        this.expireCountdown(el);
        return;
      }

      this.countdownEndsAt = Date.now() + remaining * 1000;
      this.units = {
        days: el.querySelector('[data-unit="days"]'),
        hours: el.querySelector('[data-unit="hours"]'),
        minutes: el.querySelector('[data-unit="minutes"]'),
        seconds: el.querySelector('[data-unit="seconds"]')
      };
      this.renderCountdown(el);

      var self = this;
      this.countdownInterval = setInterval(function () {
        self.renderCountdown(el);
      }, 1000);
    }

    renderCountdown(el) {
      if (!el.isConnected) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        return;
      }

      var remaining = Math.round((this.countdownEndsAt - Date.now()) / 1000);
      if (remaining <= 0) {
        this.expireCountdown(el);
        return;
      }

      var days = Math.floor(remaining / 86400);
      var hours = Math.floor((remaining % 86400) / 3600);
      var minutes = Math.floor((remaining % 3600) / 60);
      var seconds = remaining % 60;

      if (this.units.days) this.units.days.textContent = pad2(days);
      if (this.units.hours) this.units.hours.textContent = pad2(hours);
      if (this.units.minutes) this.units.minutes.textContent = pad2(minutes);
      if (this.units.seconds) this.units.seconds.textContent = pad2(seconds);
    }

    expireCountdown(el) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      var expired = (el.getAttribute('data-expired-text') || '').trim();
      if (expired) {
        el.textContent = expired;
        el.classList.add('is-expired');
      } else if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Quickview                                                           */
    /* ------------------------------------------------------------------ */

    openQuickview(trigger) {
      var handle = trigger.getAttribute('data-sbundle-qv');
      var variant = trigger.getAttribute('data-sbundle-variant');
      var url = trigger.getAttribute('data-sbundle-url');

      if (!handle) {
        if (url) window.location.href = url;
        return;
      }

      var self = this;
      var existing = document.getElementById(DIALOG_ID);
      if (existing) {
        /* EasyDialogBox.create() short-circuits on a duplicate id and returns
           the OLD dialog, so close it and wait for the node to leave the DOM. */
        this.closeOpenDialog(existing);
        this.whenDialogGone(function () {
          self.fetchQuickview(trigger, handle, variant, url);
        });
        return;
      }

      this.fetchQuickview(trigger, handle, variant, url);
    }

    closeOpenDialog(dialog) {
      var closeBtn = dialog.querySelector('.dlg-close-x');
      if (closeBtn) {
        safe(closeBtn.click.bind(closeBtn));
        return;
      }
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    }

    whenDialogGone(callback) {
      var start = Date.now();
      var step = function () {
        if (!document.getElementById(DIALOG_ID)) {
          callback();
          return;
        }
        if (Date.now() - start > 2000) {
          var stale = document.getElementById(DIALOG_ID);
          if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
          callback();
          return;
        }
        window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    }

    fetchQuickview(trigger, handle, variant, url) {
      var self = this;
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var src =
        root +
        'products/' +
        encodeURIComponent(handle) +
        '/?section_id=product-quickview' +
        (variant ? '&variant=' + encodeURIComponent(variant) : '');

      trigger.classList.add('is-busy');
      this.dialogReturnFocus = trigger;

      fetch(src)
        .then(function (response) {
          if (!response.ok) throw new Error('quickview request failed');
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var el = doc.querySelector('#shopify-section-product-quickview');
          if (!el || !window.EasyDialogBox) throw new Error('quickview unavailable');

          var box = window.EasyDialogBox.create(DIALOG_NAME, DIALOG_CLASSES, '', el.innerHTML);
          if (!box) throw new Error('dialog rejected');

          box.onClose = box.destroy;
          box.show();

          /* Same re-init list as theme.js:1620-1627, every call guarded.
             All of these are safe to re-run: the wishlist/compare binders
             register a NAMED function reference, so addEventListener de-dupes. */
          safe(function () {
            window.BlsColorSwatchesShopify && window.BlsColorSwatchesShopify.init();
          });
          safe(function () {
            window.BlsReloadSpr && window.BlsReloadSpr.init();
          });
          if (window.Shopify) {
            safe(function () {
              window.Shopify.eventFlashSold && window.Shopify.eventFlashSold('dlg');
            });
            safe(function () {
              window.Shopify.eventCountDownTimer && window.Shopify.eventCountDownTimer('dlg');
            });
            safe(function () {
              /* throws when #bls__swiper-qickview is absent */
              window.Shopify.swiperSlideQickview && window.Shopify.swiperSlideQickview();
            });
            safe(function () {
              window.Shopify.termsConditionsAction && window.Shopify.termsConditionsAction();
            });
            safe(function () {
              window.Shopify.PaymentButton &&
                window.Shopify.PaymentButton.init &&
                window.Shopify.PaymentButton.init();
            });
          }
          safe(function () {
            var sub = window.BlsSubActionProduct;
            if (!sub) return;
            /* Idempotent by construction (named handler references). We never
               touch handleInitQuickviewAction / init — those re-bind every
               product card on the page. */
            sub.handleActionWishlist && sub.handleActionWishlist();
            sub.handleInitWishlist && sub.handleInitWishlist();
            sub.handleActionCompare && sub.handleActionCompare();
            sub.handleInitCompare && sub.handleInitCompare();
          });
          safe(function () {
            window.BlsLazyloadImg && window.BlsLazyloadImg.init();
          });

          self.hardenQuickview(trigger);
        })
        .catch(function () {
          if (url) window.location.href = url;
        })
        .then(function () {
          trigger.classList.remove('is-busy');
          trigger.classList.remove('btn-loading');
          var loaders = trigger.querySelectorAll('.loader-icon');
          for (var i = 0; i < loaders.length; i++) {
            if (loaders[i].parentNode) loaders[i].parentNode.removeChild(loaders[i]);
          }
        });
    }

    /* ------------------------------------------------------------------ */
    /* Dialog accessibility hardening                                      */
    /* ------------------------------------------------------------------ */

    hardenQuickview(returnFocusTo) {
      var self = this;
      var start = Date.now();

      var step = function () {
        var dialog = document.getElementById(DIALOG_ID);
        var box = dialog ? dialog.querySelector('.dlg-box') : null;
        if (!box || !isRendered(box)) {
          if (Date.now() - start > DIALOG_WAIT_MS) return;
          window.requestAnimationFrame(step);
          return;
        }
        self.applyDialogA11y(dialog, box, returnFocusTo);
      };

      window.requestAnimationFrame(step);
    }

    applyDialogA11y(dialog, box, returnFocusTo) {
      if (box.getAttribute('data-sbundle-hardened') === 'true') return;
      box.setAttribute('data-sbundle-hardened', 'true');

      this.dialogBox = box;
      this.dialogReturnFocus = returnFocusTo || this.dialogReturnFocus;

      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', this.strDialog || STR.dialogLabel);
      box.setAttribute('tabindex', '-1');

      /* EasyDialogBox renders the close control as a bare <span>. */
      var closeBtn = box.querySelector('.dlg-close-x');
      if (closeBtn) {
        closeBtn.setAttribute('role', 'button');
        closeBtn.setAttribute('tabindex', '0');
        closeBtn.setAttribute('aria-label', this.strClose || STR.close);
        if (closeBtn.getAttribute('data-sbundle-key') !== 'true') {
          closeBtn.setAttribute('data-sbundle-key', 'true');
          closeBtn.addEventListener('keydown', function (event) {
            if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
            event.preventDefault();
            closeBtn.click();
          });
        }
      }

      this.bindDialogStockNotify(box);
      this.lockScroll();

      document.addEventListener('keydown', this.onDialogKeydown, true);

      var focusTarget =
        box.querySelector('.bls__quickview-content h1, .bls__quickview-content h2, .bls__quickview-content h3') ||
        box.querySelector('h1, h2, h3') ||
        closeBtn ||
        box;
      if (focusTarget !== closeBtn && focusTarget !== box) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      safe(function () {
        focusTarget.focus({ preventScroll: true });
      });

      this.observeDialogClose();
    }

    /**
     * The theme's own stock-notify binder (showPopupStockNotify) attaches an
     * anonymous arrow to every `.product-notify-stock` on the page, so calling
     * it would double-bind the rest of the homepage. Bind inside the dialog only.
     */
    bindDialogStockNotify(box) {
      var sub = window.BlsSubActionProduct;
      if (!sub || !sub.fetchDataStockNotifySection) return;
      var buttons = box.querySelectorAll('.product-notify-stock');
      for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        if (btn.getAttribute('data-sbundle-bound') === 'true') continue;
        btn.setAttribute('data-sbundle-bound', 'true');
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var target = event.currentTarget;
          safe(function () {
            sub.fetchDataStockNotifySection(target.getAttribute('data-product-variant'));
          });
        });
      }
    }

    onDialogKeydown(event) {
      if (event.key !== 'Tab') return;
      var box = this.dialogBox;
      if (!box || !box.isConnected) return;

      /* sections/product-quickview.liquid can open a SECOND EasyDialogBox (the
         stock-notify form). While focus lives in that dialog the trap must stand
         down, or every Tab yanks focus back into the quickview underneath. */
      var active = document.activeElement;
      var owner = active && active.closest ? active.closest('.dlg-box') : null;
      if (owner && owner !== box) return;

      var nodes = box.querySelectorAll(FOCUSABLE_SELECTOR);
      var focusables = [];
      for (var i = 0; i < nodes.length; i++) {
        if (isRendered(nodes[i])) focusables.push(nodes[i]);
      }

      if (!focusables.length) {
        event.preventDefault();
        safe(box.focus.bind(box));
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      var inside = box.contains(active);

      if (event.shiftKey) {
        if (!inside || active === first || active === box) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    observeDialogClose() {
      var self = this;

      if (!('MutationObserver' in window)) {
        /* Without the observer, releaseDialog() would never run: the document
           keydown trap would stay bound and <body> would stay position:fixed for
           the rest of the session. Poll instead. */
        var poll = window.setInterval(function () {
          var open = document.getElementById(DIALOG_ID);
          if (open && isRendered(open)) return;
          window.clearInterval(poll);
          self.dialogPoll = null;
          self.releaseDialog(true);
        }, 250);
        this.dialogPoll = poll;
        return;
      }

      if (this.dialogObserver) {
        safe(this.dialogObserver.disconnect.bind(this.dialogObserver));
        this.dialogObserver = null;
      }

      var check = function () {
        var dialog = document.getElementById(DIALOG_ID);
        if (dialog && isRendered(dialog)) return;
        self.releaseDialog(true);
      };

      this.dialogObserver = new MutationObserver(check);
      /* EasyDialogBox removes the node ~400ms after destroy(); it also mutates
         style/class on the way out, so watch both. */
      this.dialogObserver.observe(document.body, { childList: true });
      var dialog = document.getElementById(DIALOG_ID);
      if (dialog) {
        this.dialogObserver.observe(dialog, {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }
    }

    releaseDialog(restoreFocus) {
      if (this.dialogPoll) {
        window.clearInterval(this.dialogPoll);
        this.dialogPoll = null;
      }
      if (this.dialogObserver) {
        safe(this.dialogObserver.disconnect.bind(this.dialogObserver));
        this.dialogObserver = null;
      }
      document.removeEventListener('keydown', this.onDialogKeydown, true);
      this.dialogBox = null;
      this.unlockScroll();

      var trigger = this.dialogReturnFocus;
      this.dialogReturnFocus = null;
      if (restoreFocus && trigger && trigger.isConnected && trigger.focus) {
        safe(function () {
          trigger.focus({ preventScroll: true });
        });
      }
    }

    /* ------------------------------------------------------------------ */
    /* Background scroll lock                                              */
    /* ------------------------------------------------------------------ */

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;

      var body = document.body;
      this.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.bodyStyle = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight
      };

      var gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.classList.add('sbundle-scroll-lock');
      body.style.position = 'fixed';
      body.style.top = -this.scrollY + 'px';
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (gap > 0) body.style.paddingRight = gap + 'px';
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;

      var body = document.body;
      var prev = this.bodyStyle || {};
      body.style.position = prev.position || '';
      body.style.top = prev.top || '';
      body.style.left = prev.left || '';
      body.style.right = prev.right || '';
      body.style.width = prev.width || '';
      body.style.overflow = prev.overflow || '';
      body.style.paddingRight = prev.paddingRight || '';
      document.documentElement.classList.remove('sbundle-scroll-lock');
      this.bodyStyle = null;

      var y = this.scrollY;
      safe(function () {
        window.scrollTo(0, y);
      });
    }

    /* ------------------------------------------------------------------ */
    /* Add both to cart                                                    */
    /* ------------------------------------------------------------------ */

    onChange(event) {
      var field = closestIn(event.target, '[data-sbundle-size]');
      if (!field || !this.contains(field)) return;
      this.updateAddBothState();
    }

    updateAddBothState() {
      var form = this.querySelector('[data-sbundle-form]');
      if (!form) return;
      var button = form.querySelector('[data-sbundle-add-both]');
      if (!button) return;

      var ids = form.querySelectorAll('[name="items[][id]"]');
      if (!ids.length) {
        button.disabled = true;
        return;
      }

      var required = form.querySelectorAll('[data-sbundle-size][data-required="true"]');
      var ready = true;
      for (var i = 0; i < required.length; i++) {
        if (!required[i].value) {
          ready = false;
          break;
        }
      }
      button.disabled = !ready || this.isAdding;
    }

    itemLabels() {
      var labels = [];
      var nodes = this.querySelectorAll('.sbundle__size-label');
      for (var i = 0; i < nodes.length; i++) {
        var text = (nodes[i].textContent || '').trim();
        if (text) labels.push(text);
      }
      return labels;
    }

    setFeedback(message, isError) {
      var el = this.querySelector('[data-sbundle-feedback]');
      if (!el) return;
      el.textContent = message || '';
      el.classList.toggle('has-error', !!isError);
    }

    onSubmit(event) {
      var form = closestIn(event.target, '[data-sbundle-form]');
      if (!form || !this.contains(form)) return;

      event.preventDefault();
      if (this.isAdding) return;

      var button = form.querySelector('[data-sbundle-add-both]');
      var ids = form.querySelectorAll('[name="items[][id]"]');
      if (!ids.length) return;

      var required = form.querySelectorAll('[data-sbundle-size][data-required="true"]');
      for (var i = 0; i < required.length; i++) {
        if (!required[i].value) {
          this.setFeedback('', false);
          safe(required[i].focus.bind(required[i]));
          return;
        }
      }

      this.isAdding = true;
      this.setFeedback('', false);
      if (button) {
        button.classList.add('btn-loading');
        button.disabled = true;
      }

      var self = this;
      var cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var formData = new FormData(form);

      if (cart && typeof cart.getSectionsToRender === 'function') {
        safe(function () {
          formData.append(
            'sections',
            cart.getSectionsToRender().map(function (section) {
              return section.id;
            })
          );
          formData.append('sections_url', window.location.pathname);
        });
      }

      var addUrl =
        (window.routes && window.routes.cart_add_url) ||
        ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') +
          'cart/add';

      /* No Content-Type header — the browser must set the multipart boundary. */
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(function (response) {
          return response.text().then(function (text) {
            return { ok: response.ok, text: text };
          });
        })
        .then(function (result) {
          var state = null;
          try {
            state = JSON.parse(result.text);
          } catch (e) {
            state = null;
          }

          if (!result.ok || !state || state.status || state.errors) {
            throw self.cartErrorMessage(state);
          }

          self.refreshCart(state, cart);
          self.setFeedback(STR.added, false);
        })
        .catch(function (error) {
          var message = typeof error === 'string' ? error : STR.genericError;
          self.setFeedback(message, true);
        })
        .then(function () {
          self.isAdding = false;
          if (button) button.classList.remove('btn-loading');
          self.updateAddBothState();
          /* No BlsLazyloadImg.init() here: assets/global.js builds a brand-new
             page-wide IntersectionObserver on every call and never disconnects
             the old one, and refreshCart() already re-inits after injecting the
             mini-cart markup. */
        });
    }

    /**
     * /cart/add.js with items[] is atomic: one unavailable line fails the whole
     * request, so the message must name what could not be added.
     */
    cartErrorMessage(state) {
      if (state) {
        if (typeof state.description === 'string' && state.description) return state.description;
        if (typeof state.message === 'string' && state.message && state.message !== 'Cart Error') {
          return state.message;
        }
        if (state.errors && typeof state.errors === 'object') {
          var parts = [];
          for (var key in state.errors) {
            if (!Object.prototype.hasOwnProperty.call(state.errors, key)) continue;
            var value = state.errors[key];
            parts.push(key + ': ' + (Array.isArray(value) ? value.join(', ') : value));
          }
          if (parts.length) return parts.join(' ');
        }
      }
      var labels = this.itemLabels();
      if (labels.length) return STR.namedError + labels.join(', ') + '.';
      return STR.genericError;
    }

    refreshCart(state, cart) {
      if (cart && state.sections && typeof cart.getSectionsToRender === 'function') {
        var sections = cart.getSectionsToRender();
        for (var i = 0; i < sections.length; i++) {
          var id = sections[i].id;
          var target = document.getElementById(id);
          var markup = state.sections[id];
          if (!target || !markup) continue;

          var doc = new DOMParser().parseFromString(markup, 'text/html');
          var source = doc.querySelector('#form-mini-cart');
          if (!source) continue;
          target.innerHTML = source.innerHTML;

          var countdown = cart.querySelector ? cart.querySelector('.cart-countdown-time') : null;
          var nextCountdown = doc.querySelector('.cart-countdown-time');
          if (countdown && nextCountdown) {
            countdown.innerHTML = nextCountdown.innerHTML;
            safe(function () {
              cart.countdownTimer && cart.countdownTimer();
            });
          }
        }

        safe(function () {
          cart.cartAction && cart.cartAction();
        });
        safe(function () {
          cart.open && cart.open();
        });
      }

      fetch('/cart.json')
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (!data || data.item_count === undefined) return;

          var counts = document.querySelectorAll('.cart-count');
          for (var i = 0; i < counts.length; i++) {
            if (counts[i].classList.contains('cart-count-drawer')) {
              counts[i].innerHTML = '(' + data.item_count + ')';
            } else {
              counts[i].innerHTML = data.item_count;
            }
          }

          var totalPrice = document.querySelector('header-total-price');
          if (totalPrice && totalPrice.updateTotal) totalPrice.updateTotal(data);

          var freeShip = document.querySelector('free-ship-progress-bar');
          if (freeShip && freeShip.init) freeShip.init(data.items_subtotal_price);
        })
        .catch(function () {
          /* the mini-cart markup is already refreshed; a stale badge is survivable */
        });
    }

    /* ------------------------------------------------------------------ */
    /* Theme editor block selection                                        */
    /* ------------------------------------------------------------------ */

    /* ================================================================== */
    /* Pin panel                                                          */
    /* ================================================================== */

    /**
     * True when a pin tap should reveal the inline panel rather than open the
     * quick view dialog. The mobile answer is a separate merchant setting: there
     * is no space beside the collage on a phone, so a store can choose to fall
     * back to the dialog there.
     */
    panelMode() {
      if (this.getAttribute('data-pin-action') !== 'panel') return false;
      if (!this.querySelector('[data-sbundle-panels]')) return false;
      if (
        this.getAttribute('data-panel-mobile') === 'dialog' &&
        media('(max-width: 767px)')
      ) {
        return false;
      }
      return true;
    }

    setupPanels() {
      var panels = this.querySelectorAll('[data-sbundle-panel]');
      for (var i = 0; i < panels.length; i++) this.syncPanel(panels[i]);
    }

    /** Parsed variant JSON, cached on the element after the first read. */
    panelData(panel) {
      if (panel.sbundleData) return panel.sbundleData;
      var node = panel.querySelector('[data-sbundle-variants]');
      var data = { single: false, variants: [] };
      if (node) {
        try {
          var parsed = JSON.parse(node.textContent);
          if (parsed && Array.isArray(parsed.variants)) data = parsed;
        } catch (e) {
          /* A malformed payload must not take the section down: the panel still
             shows the photo, title, price and the link to the product page. */
          data = { single: false, variants: [] };
        }
      }
      panel.sbundleData = data;
      return data;
    }

    /** The option values currently chosen, indexed by option position - 1. */
    panelSelection(panel) {
      var groups = panel.querySelectorAll('[data-sbundle-opt-group]');
      var chosen = [];
      for (var i = 0; i < groups.length; i++) {
        var pos = parseInt(groups[i].getAttribute('data-sbundle-opt-group'), 10);
        var sel = groups[i].querySelector('.sbundle__opt.is-selected');
        if (!isNaN(pos)) chosen[pos - 1] = sel ? sel.getAttribute('data-value') : null;
      }
      return chosen;
    }

    /**
     * Recomputes which option values can still be reached, which variant the
     * current selection resolves to, and whether the add button may fire.
     * A value that leads nowhere is disabled and struck through rather than
     * removed -- hiding it reads as "this size was never made".
     */
    syncPanel(panel) {
      var data = this.panelData(panel);
      var chosen = this.panelSelection(panel);
      var i;
      var j;

      /* Per-value reachability, judged against every OTHER chosen option. */
      var buttons = panel.querySelectorAll('.sbundle__opt');
      for (i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        var pos = parseInt(btn.getAttribute('data-sbundle-opt'), 10);
        if (isNaN(pos)) continue;
        var value = btn.getAttribute('data-value');
        var reachable = false;

        for (j = 0; j < data.variants.length; j++) {
          var vr = data.variants[j];
          if (!vr.available) continue;
          if (!vr.options || vr.options[pos - 1] !== value) continue;
          var ok = true;
          for (var k = 0; k < chosen.length; k++) {
            if (k === pos - 1) continue;
            if (chosen[k] && vr.options[k] !== chosen[k]) {
              ok = false;
              break;
            }
          }
          if (ok) {
            reachable = true;
            break;
          }
        }

        btn.classList.toggle('is-unavailable', !reachable);
        btn.setAttribute('aria-disabled', reachable ? 'false' : 'true');
      }

      /* Echo the chosen value next to the option name. */
      var groups = panel.querySelectorAll('[data-sbundle-opt-group]');
      for (i = 0; i < groups.length; i++) {
        var out = groups[i].querySelector('[data-sbundle-opt-selected]');
        var pick = groups[i].querySelector('.sbundle__opt.is-selected');
        if (out) out.textContent = pick ? pick.getAttribute('data-value') : '';
      }

      /* Resolve the variant, but only once every group has an answer. */
      var complete = true;
      for (i = 0; i < chosen.length; i++) {
        if (!chosen[i]) {
          complete = false;
          break;
        }
      }

      var match = null;
      if (data.single) {
        match = data.variants.length ? data.variants[0] : null;
      } else if (complete) {
        for (j = 0; j < data.variants.length; j++) {
          var cand = data.variants[j];
          var hit = true;
          for (i = 0; i < chosen.length; i++) {
            if (cand.options[i] !== chosen[i]) {
              hit = false;
              break;
            }
          }
          if (hit) {
            match = cand;
            break;
          }
        }
      }

      var addBtn = panel.querySelector('[data-sbundle-panel-add]');
      if (addBtn) {
        var ready = !!(match && match.available);
        addBtn.classList.toggle('is-ready', ready);
        addBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
        if (match) addBtn.setAttribute('data-variant', match.id);
      }

      return match;
    }

    selectOption(btn) {
      var panel = closestIn(btn, '[data-sbundle-panel]');
      if (!panel) return;

      /* A struck-through value is still focusable so a keyboard user can read it,
         but choosing it would resolve to nothing. */
      if (btn.getAttribute('aria-disabled') === 'true') return;

      var group = closestIn(btn, '[data-sbundle-opt-group]');
      if (group) {
        var siblings = group.querySelectorAll('.sbundle__opt');
        for (var i = 0; i < siblings.length; i++) {
          siblings[i].classList.remove('is-selected');
          siblings[i].setAttribute('aria-pressed', 'false');
        }
      }
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      var match = this.syncPanel(panel);

      /* "Tapping a size adds to the cart straight away" -- but only once the
         selection is unambiguous, so a colour + size product still needs both. */
      var addBtn = panel.querySelector('[data-sbundle-panel-add]');
      if (
        addBtn &&
        addBtn.getAttribute('data-auto-add') === 'true' &&
        match &&
        match.available
      ) {
        this.addPanelVariant(addBtn);
      }
    }

    panelFor(handle) {
      if (!handle) return null;
      var panels = this.querySelectorAll('[data-sbundle-panel]');
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-sbundle-panel') === handle) return panels[i];
      }
      return null;
    }

    activatePanel(trigger) {
      var handle =
        trigger.getAttribute('data-sbundle-panel-target') ||
        trigger.getAttribute('data-sbundle-qv');
      var panel = this.panelFor(handle);

      /* No panel for this product (a pin pointing at a product the panels loop
         skipped) -- fall back to the dialog rather than doing nothing. */
      if (!panel) {
        this.openQuickview(trigger);
        return;
      }

      var panels = this.querySelectorAll('[data-sbundle-panel]');
      for (var i = 0; i < panels.length; i++) {
        var on = panels[i] === panel;
        panels[i].classList.toggle('is-open', on);
        if (on) panels[i].removeAttribute('hidden');
        else panels[i].setAttribute('hidden', '');
      }

      var idx = trigger.getAttribute('data-sbundle-pin') || trigger.getAttribute('data-sbundle-row-cta');
      this.setActiveIndex(idx || null);

      var triggers = this.querySelectorAll('[data-sbundle-panel-target]');
      for (var j = 0; j < triggers.length; j++) {
        triggers[j].setAttribute('aria-expanded', triggers[j] === trigger ? 'true' : 'false');
      }

      this.syncPanel(panel);
      this.setFeedback('', false);

      /* On a phone the panel sits below the collage, so it is off-screen at the
         moment of the tap. Bring it into view; on desktop it is already beside
         the photo and scrolling would be disorienting. */
      if (media('(max-width: 767px)')) {
        safe(function () {
          panel.scrollIntoView({
            block: 'nearest',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        });
      }

      /* Move focus to the panel so a keyboard or screen-reader user lands on
         what just appeared. tabindex is set only for this programmatic focus and
         removed on blur, so the panel never becomes a stray tab stop. */
      safe(function () {
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
        panel.addEventListener(
          'blur',
          function () {
            panel.removeAttribute('tabindex');
          },
          { once: true }
        );
      });
    }

    /**
     * Adds the resolved variant. Reuses the same cart plumbing as "Add both to
     * cart" -- one code path for errors, mini-cart refresh and feedback.
     */
    addPanelVariant(button) {
      if (this.isAdding) return;
      if (button.getAttribute('aria-disabled') === 'true') return;

      var panel = closestIn(button, '[data-sbundle-panel]');
      if (!panel) return;

      var match = this.syncPanel(panel);
      if (!match || !match.available) {
        /* Nothing resolved yet: send focus to the first group still unanswered
           instead of failing silently. */
        var groups = panel.querySelectorAll('[data-sbundle-opt-group]');
        for (var g = 0; g < groups.length; g++) {
          if (groups[g].querySelector('.sbundle__opt.is-selected')) continue;
          var first = groups[g].querySelector('.sbundle__opt:not(.is-unavailable)');
          if (first) safe(first.focus.bind(first));
          return;
        }
        return;
      }

      var self = this;
      var cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      var formData = new FormData();
      formData.append('id', match.id);
      formData.append('quantity', 1);

      if (cart && typeof cart.getSectionsToRender === 'function') {
        safe(function () {
          formData.append(
            'sections',
            cart.getSectionsToRender().map(function (section) {
              return section.id;
            })
          );
          formData.append('sections_url', window.location.pathname);
        });
      }

      var addUrl =
        (window.routes && window.routes.cart_add_url) ||
        ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') +
          'cart/add';

      this.isAdding = true;
      this.setFeedback('', false);
      button.classList.add('btn-loading');

      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (response) {
          return response.text().then(function (text) {
            return { ok: response.ok, text: text };
          });
        })
        .then(function (result) {
          var state = null;
          try {
            state = JSON.parse(result.text);
          } catch (e) {
            state = null;
          }
          if (!result.ok || !state || state.status || state.errors) {
            throw self.cartErrorMessage(state);
          }
          self.refreshCart(state, cart);
          self.setFeedback(STR.added, false);
        })
        .catch(function (error) {
          var message = typeof error === 'string' ? error : STR.genericError;
          self.setFeedback(message, true);
        })
        .then(function () {
          self.isAdding = false;
          button.classList.remove('btn-loading');
        });
    }

    onBlockSelect(event) {
      var anchor = closestIn(event.target, '.sbundle__pin-anchor');
      if (!anchor || !this.contains(anchor)) return;
      var pin = anchor.querySelector('[data-sbundle-pin]');
      this.setActiveIndex(pin ? pin.getAttribute('data-sbundle-pin') : null);
      if (!pin) anchor.classList.add('is-active');

      /* Show the panel for the pin the merchant just clicked in the sidebar, so
         the panel settings can be judged against a real product. */
      if (pin && this.panelMode()) this.activatePanel(pin);

      /* Placing a pin is the fiddliest part of the weekly job: scroll the pin the
         merchant just selected in the sidebar into view so the slider has a
         visible effect. */
      safe(function () {
        anchor.scrollIntoView({
          block: 'center',
          inline: 'center',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      });
    }

    onBlockDeselect(event) {
      var anchor = closestIn(event.target, '.sbundle__pin-anchor');
      if (!anchor || !this.contains(anchor)) return;
      anchor.classList.remove('is-active');
      this.clearActive();
    }
  }

  window.customElements.define('shoppable-bundle', ShoppableBundle);
})();
